import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, CardHeader, Col, Row } from 'reactstrap'
import { ILocation } from './index'
import { toast } from 'react-toastify'
import DeleteModal from 'Components/Common/DeleteModal'
import { useDebouncedCallback } from 'use-debounce'
import { Spinner } from 'reactstrap'
import { ISort } from 'lib/type'

interface IProds {
  fetching: boolean
  locationRecords: ILocation[]
  handleOpenDetails: (arg: string) => void
  setOpenLocationPopup: (arg: boolean) => void
  setEditMode: (arg: boolean) => void
  handleDeleteLocation: (arg: string) => Promise<string>
  searchLocation: (arg: string) => void
  sortAction: ISort
  setSortAction: (arg: ISort) => void
  setCanEdit: (arg: boolean) => void
}

const LocationsTable: React.FC<IProds> = ({
  locationRecords,
  handleOpenDetails,
  setOpenLocationPopup,
  setEditMode,
  fetching,
  handleDeleteLocation,
  searchLocation,
  sortAction,
  setSortAction,
  setCanEdit
}) => {
  const [showModalDelete, setShowModalDelete] = useState<boolean>(false)
  const [locationSelectedId, setLocationSelectedId] = useState<string | undefined>(undefined)
  const [query, setQuery] = useState('')
  const [permissions, setPermissions] = useState({
    canCreate: true,
    canEdit: true,
    canDelete: true
  })

  const handleAddLocation = () => {
    setEditMode(false)
    setOpenLocationPopup(true)
  }

  const handleEditLocation = (locationId: string | undefined) => {
    const id = locationId as string
    setEditMode(true)
    setOpenLocationPopup(true)
    handleOpenDetails(id)
  }

  const handleDeletLocationRecord = async (boothId: string | undefined) => {
    const id = boothId as string
    const res = handleDeleteLocation(id)

    const message = await res
    if (message === 'SUCCESSFULL') {
      toast.success('Deleted a location successfully')
    }
  }

  const handleShowModalDelete = (locationId: string | undefined) => {
    const id = locationId as string
    setShowModalDelete(true)
    setLocationSelectedId(id)
  }

  const handleDelete = () => {
    handleDeletLocationRecord(locationSelectedId)
    setShowModalDelete(false)
  }

  const debouncedSearch = useDebouncedCallback((searchTerm: string) => {
    searchLocation(searchTerm)
  }, 1000)

  const handleInputChange = (e: { target: { name: any; value: any } }) => {
    const value = e.target.value
    setQuery(value)
    debouncedSearch(value)
  }

  const handleActionSort = (sortBy: string) => {
    if (sortAction.sortBy === sortBy) {
      setSortAction({
        ...sortAction,
        sortDirection: sortAction.sortDirection === 'DESC' ? 'ASC' : 'DESC'
      })
    } else {
      setSortAction({
        sortDirection: 'DESC',
        sortBy: sortBy
      })
    }
  }

  useEffect(() => {
    const userType = sessionStorage.getItem('userType')

    // Type 1 = Admin - full access to everything
    if (userType === '1') {
      setPermissions({
        canCreate: true,
        canEdit: true,
        canDelete: true
      })
      setCanEdit(true)
      return
    }

    // Type 2 = Regular user - use role-based permissions
    const userRoles = sessionStorage.getItem('userRoles')
      ? JSON.parse(atob(sessionStorage.getItem('userRoles')!))
      : null
    if (userRoles) {
      userRoles.forEach((role: any) => {
        if (role.page === 'Locations') {
          setPermissions({
            canCreate: role.canCreate,
            canEdit: role.canEdit,
            canDelete: role.canDelete
          })
          setCanEdit(role.canEdit)
        }
      })
    }
  }, [setCanEdit])

  return (
    <React.Fragment>
      <Row>
        <Col xl={12}>
          <Card>
            <CardHeader className='align-items-center d-flex justify-content-between'>
              <div className='search-box d-inline-block col-8 d-flex align-items-center'>
                <input
                  id='search-bar-location'
                  className='form-control search'
                  placeholder='Search locations'
                  onChange={(e) => handleInputChange(e)}
                  value={query}
                />
                <i className='bx bx-search-alt search-icon'></i>
              </div>
              {permissions.canCreate && (
                <Button className='p-2' color='primary' onClick={(e) => handleAddLocation()}>
                  <div className='flex align-items-center'>
                    {fetching ? (
                      <Spinner size='sm'> Loading... </Spinner>
                    ) : (
                      <>
                        <i className='mdi mdi-plus'></i>
                        <span className='fw-semibold ms-1'>Add Location</span>
                      </>
                    )}
                  </div>
                </Button>
              )}
            </CardHeader>
            <CardBody>
              <div className='table-responsive table-card'>
                <table className='table align-middle table-nowrap table-striped-columns mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th scope='col' onClick={() => handleActionSort('id')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>ID</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'id' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'id' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('name')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Name</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'name' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'name' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('city')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>City</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'city' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'city' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' onClick={() => handleActionSort('address')}>
                        <div className='w-100 d-flex justify-content-between'>
                          <span>Address</span>
                          <i
                            className={`mdi ${sortAction.sortDirection === 'ASC' && sortAction.sortBy === 'address' ? 'mdi-arrow-down-thin' : 'mdi-arrow-up-thin'}
                            ${sortAction.sortBy !== 'address' && 'opacity-25'}`}
                          ></i>
                        </div>
                      </th>
                      <th scope='col' style={{ width: '150px' }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {locationRecords.map((location, index) => {
                      return (
                        <tr key={index}>
                          <td>
                            <Link to='#' className='fw-medium'>
                              {location._id}
                            </Link>
                          </td>
                          <td>{location.name}</td>
                          <td>{location.city}</td>
                          <td>{location.address}</td>
                          <td>
                            <button
                              type='button'
                              className='btn btn-sm btn-light'
                              onClick={(e) => handleEditLocation(location?._id)}
                            >
                              Details
                            </button>
                            {permissions.canDelete && (
                              <button
                                type='button'
                                className='btn btn-sm btn-danger ms-2'
                                onClick={(e) => handleShowModalDelete(location?._id)}
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>
      <DeleteModal
        show={showModalDelete}
        onDeleteClick={() => handleDelete()}
        onCloseClick={() => setShowModalDelete(false)}
      />
    </React.Fragment>
  )
}
export default LocationsTable
